// Display-currency context for scenario outputs.
//
// Engine results are always expressed in the model's base currency. This
// context holds the currency the user is *viewing* in and exposes the
// multiplier to get there, so output cards can convert at the moment they
// render.
//
// Important: convert once, at the final formatting call. Cards that do their
// own pricing math (CostAnalysisCard, SummaryView) read rates straight off the
// model, which are already in base currency — converting those intermediate
// rates as well would double-apply the factor.

import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  conversionFactor,
  defaultDisplayCurrency,
  displayCurrencyOptions,
  modelCurrency,
} from '@rc/engine';
import type { CurrencyCode, Model } from '@rc/engine/types';
import { fmtMoney, fmtMoneyExact } from './format';

interface CurrencyContextValue {
  /** Currency the model's stored numbers are in. */
  base: CurrencyCode;
  /** Currency the user is viewing in. */
  display: CurrencyCode;
  setDisplay: (code: CurrencyCode) => void;
  /** Currencies this model may be displayed in. */
  options: CurrencyCode[];
  /** Multiplier from base into display. 1 when they match. */
  factor: number;
  /** Convert a base-currency amount into the display currency. */
  convert: (n: number) => number;
  /** Convert and format, abbreviated (e.g. GBP 1.2M). */
  money: (n: number) => string;
  /** Convert and format at full precision, for tooltips. */
  moneyExact: (n: number) => string;
}

const FALLBACK: CurrencyContextValue = {
  base: 'USD',
  display: 'USD',
  setDisplay: () => {},
  options: ['USD'],
  factor: 1,
  convert: (n) => n,
  money: (n) => fmtMoney(n, 'USD'),
  moneyExact: (n) => fmtMoneyExact(n, 'USD'),
};

const CurrencyContext = createContext<CurrencyContextValue>(FALLBACK);

export function CurrencyProvider({
  model,
  children,
}: {
  model: Model;
  children: ReactNode;
}) {
  const base = modelCurrency(model).base;
  const options = useMemo(() => displayCurrencyOptions(model), [model]);
  const modelDefault = useMemo(() => defaultDisplayCurrency(model), [model]);
  const [override, setOverride] = useState<CurrencyCode | null>(null);

  // The model's default wins until the user picks something else. An override
  // that the model no longer offers (edited mid-session) falls back cleanly.
  const display = override && options.includes(override) ? override : modelDefault;

  const value = useMemo<CurrencyContextValue>(() => {
    const factor = conversionFactor(model, display);
    return {
      base,
      display,
      setDisplay: setOverride,
      options,
      factor,
      convert: (n: number) => n * factor,
      money: (n: number) => fmtMoney(n * factor, display),
      moneyExact: (n: number) => fmtMoneyExact(n * factor, display),
    };
  }, [model, base, display, options]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

/**
 * Display-currency helpers. Safe to call outside a provider, where it behaves
 * as plain USD formatting.
 */
export function useCurrency(): CurrencyContextValue {
  return useContext(CurrencyContext);
}
